import { urlBuilder } from '@starjack/utils';
import { fetchAuthWrapper } from '@starjack/api';
import type { PostLoginResponse } from '../lib/services/authService';

const authUrls = {
    login: urlBuilder('auth', 'login'),
    refresh: urlBuilder('auth', 'refresh'),
};

/**
 * Login to the authentication API.
 *
 * @param username - Username for login
 * @param password - Password for login
 * @param options - Optional configuration
 * @param options.isB2B - If true, uses 'b2b' as username field, otherwise 'liftOperator'
 * @returns Promise resolving to PostLoginResponse
 * @throws Error if login fails
 */
export const postLogin = async (
    username: string,
    password: string,
    options?: { isB2B?: boolean }
): Promise<PostLoginResponse> => {
    const usernameField = options?.isB2B ? 'b2b' : 'liftOperator';

    const response = await fetch(authUrls.login, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
            [usernameField]: username,
            password,
        }),
    });

    if (response.ok) {
        return await response.json();
    } else {
        if (response.status >= 400 && response.status < 500) {
            throw await response.json();
        } else {
            throw new Error('Server error');
        }
    }
};

/**
 * Refresh authentication token.
 *
 * @param refreshCode - Refresh token code
 * @returns Promise resolving to PostLoginResponse
 * @throws Error if refresh fails
 */
export const postRefresh = async (
    refreshCode: string
): Promise<PostLoginResponse> => {
    const response = await fetchAuthWrapper(authUrls.refresh, {
        method: 'POST',
        body: JSON.stringify({
            refreshCode,
            rememberMe: true,
        }),
    });

    if (response.ok) {
        return await response.json();
    } else {
        if (response.status >= 400 && response.status < 500) {
            throw await response.json();
        } else {
            throw new Error('Server error');
        }
    }
};
